import { useState } from 'react';
import { Alert, App, Button, Input, Modal, Segmented, Space, Tag } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { api } from '../../api/client';
import { Can } from '../../rbac/RequirePermission';
import { formatHandle } from '../../lib/listenerName';

/** The API's minimum for the audit reason (`POST …/gender`). */
export const GENDER_REASON_MIN = 10;

export type SwitchableGender = 'male' | 'female';

const GENDER_LABEL: Record<string, string> = {
  male: 'Male',
  female: 'Female',
  nonbinary: 'Non-binary',
  undisclosed: 'Undisclosed',
};

/**
 * Admin gender switch (male ⇄ female) on the user drawer. Shows the current
 * value and, for `users.set_gender` holders, a reason-gated modal with a
 * Male / Female toggle (same shape as the verification-admin flag). Only the
 * profile gender changes — avatar, handle and listener profile stay.
 */
export function GenderControl({
  userId,
  handle,
  label,
  gender,
  onChanged,
}: {
  userId: string;
  handle: string;
  /** How to name the user in the confirm title ("Ravi (@ravi)"); defaults to the @handle. */
  label?: string;
  gender: string | null;
  /** Called after a successful change so the owner refetches the user. */
  onChanged: () => void;
}) {
  const { message } = App.useApp();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [next, setNext] = useState<SwitchableGender>(gender === 'female' ? 'male' : 'female');

  function show() {
    // Default the toggle to the OTHER option so the common "picked wrong" fix is one click.
    setNext(gender === 'female' ? 'male' : 'female');
    setOpen(true);
  }
  function close() {
    setOpen(false);
    setReason('');
  }

  const set = useMutation({
    mutationFn: async (body: { gender: SwitchableGender; reason: string }) => {
      await api.post(`/admin/users/${userId}/gender`, body);
    },
    onSuccess: (_d, body) => {
      message.success(`Gender changed to ${GENDER_LABEL[body.gender]}`);
      close();
      onChanged();
    },
    onError: (e: unknown) => {
      const msg =
        (e as { response?: { data?: { message?: string } } }).response?.data?.message ??
        'Could not change gender';
      message.error(msg);
    },
  });

  const unchanged = next === gender;
  const who = label ?? formatHandle(handle) ?? 'this user';

  return (
    <>
      <Space size={8} wrap>
        <Tag color={gender === 'female' ? 'magenta' : gender === 'male' ? 'blue' : 'default'}>
          {gender ? (GENDER_LABEL[gender] ?? gender) : '—'}
        </Tag>
        <Can permission="users.set_gender">
          <Button size="small" onClick={show}>
            Change gender
          </Button>
        </Can>
      </Space>

      <Modal
        title={`Change gender for ${who}?`}
        open={open}
        onCancel={close}
        onOk={() => set.mutate({ gender: next, reason: reason.trim() })}
        okText="Change"
        okButtonProps={{
          disabled: unchanged || reason.trim().length < GENDER_REASON_MIN,
          loading: set.isPending,
        }}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Segmented<SwitchableGender>
            block
            value={next}
            onChange={(v) => setNext(v)}
            options={[
              { label: 'Male', value: 'male' },
              { label: 'Female', value: 'female' },
            ]}
          />
          <Alert
            type="warning"
            showIcon
            message="Changes who this user sees and is seen by"
            description={
              <span style={{ fontSize: 12 }}>
                Quick Connect and the buddy feed are filtered by gender. The avatar, handle and any
                listener profile are left as they are — fix those separately if needed. The app
                picks the new value up on its next profile refresh.
              </span>
            }
          />
          <Input.TextArea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={`Reason (≥ ${GENDER_REASON_MIN} chars) — captured in the audit log.`}
          />
        </Space>
      </Modal>
    </>
  );
}
